// Quick count on the handheld: scan a bin (or an item) and count what's there now. A match is recorded, a difference
// up to the location's limit is posted in Business Central right away, and a bigger one goes to the lead to review.
// Bins a short pick flagged are listed to check, and counting one clears its flag.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { wmsCounts, lotText, money, type QuickLookup, type QuickResult, type QuickRow } from "../counts/countsApi";
import { wms } from "./wmsApi";
import { Alert, Header, I, LocChip, ScanField, feedback, fmt, errText } from "./ui";

type Row = { binCode: string; itemNo: string; variantCode: string; lotNo: string; serialNo: string; description: string; uom: string; expected: number | null; counted: string; added?: boolean };
const keyOf = (r: { binCode: string; itemNo: string; variantCode: string; lotNo: string; serialNo: string }) => [r.binCode, r.itemNo, r.variantCode, r.lotNo, r.serialNo].join("|");

export function QuickCountPage({ loc }: { loc: string }) {
  const [sp, setSp] = useSearchParams();
  const [look, setLook] = useState<QuickLookup | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<QuickResult | null>(null);
  const [replaces, setReplaces] = useState<string | null>(null);
  const [flags, setFlags] = useState<{ binCode: string; itemNo: string | null; source: string | null }[]>([]);
  const [recent, setRecent] = useState<QuickRow[]>([]);
  const [adding, setAdding] = useState<{ itemNo: string; binCode: string; lotNo: string; needLot: boolean } | null>(null);

  useEffect(() => {
    wmsCounts.flags().then((f) => {
      const byBin = new Map<string, { binCode: string; itemNo: string | null; source: string | null }>();
      for (const x of f.filter((x) => x.locationCode === loc)) if (!byBin.has(x.binCode)) byBin.set(x.binCode, { binCode: x.binCode, itemNo: x.itemNo, source: x.source });
      setFlags([...byBin.values()]);
    }).catch(() => {});
    wmsCounts.quickRecent().then((r) => setRecent(r.filter((x) => x.location === loc))).catch(() => {});
  }, [loc, result]);

  const scan = async (code: string) => {
    setBusy(true); setErr(null); setResult(null); setAdding(null);
    try {
      const r = await wmsCounts.quickLookup(loc, code);
      feedback.ok(); setLook(r);
      setRows(r.lines.map((l) => ({ ...l, counted: "" })));
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  };
  useEffect(() => { const c = sp.get("code"); if (c) { scan(c); setSp({}, { replace: true }); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (i: number, v: string) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, counted: v } : r)));
  const allEntered = rows.length > 0 && rows.every((r) => r.counted.trim() !== "" && Number.isFinite(Number(r.counted)) && Number(r.counted) >= 0);
  const reset = () => { setLook(null); setRows([]); setResult(null); setReplaces(null); setErr(null); setAdding(null); };

  const submit = async () => {
    if (!look) return;
    setBusy(true); setErr(null);
    try {
      const r = await wmsCounts.quickSubmit({
        location: loc, ...(look.scope.kind === "bin" ? { binCode: look.scope.binCode } : { itemNo: look.scope.itemNo }),
        lines: rows.map((x) => ({ binCode: x.binCode, itemNo: x.itemNo, variantCode: x.variantCode, lotNo: x.lotNo, serialNo: x.serialNo, counted: Number(x.counted), description: x.description, uom: x.uom })),
        replaces: replaces ?? undefined,
      });
      r.status === "review" ? feedback.bad() : feedback.done();
      setResult(r);
    } catch (e) {
      feedback.bad();
      setErr(errText(e));
    } finally { setBusy(false); }
  };

  /** Something found that isn't on the list: in a bin, an item; for an item, the bin it's in. */
  const scanExtra = async (code: string) => {
    if (!look) return;
    setErr(null);
    const up = code.toUpperCase();
    if (adding?.needLot) { add({ ...adding, lotNo: up }); return; }
    setBusy(true);
    try {
      const r = await wms.scan(loc, code);
      if (look.scope.kind === "bin") {
        if (r.kind !== "item") { feedback.bad(); setErr(`Scan the item you found in ${look.scope.binCode}.`); return; }
        const tracked = r.bins.some((b) => b.itemNo === r.item.itemNo && (b.lotNo || b.serialNo));
        feedback.ok();
        if (tracked) { setAdding({ itemNo: r.item.itemNo, binCode: look.scope.binCode, lotNo: "", needLot: true }); return; }
        add({ itemNo: r.item.itemNo, binCode: look.scope.binCode, lotNo: "", needLot: false }, r.item.description, r.item.uom);
      } else {
        if (r.kind !== "bin") { feedback.bad(); setErr(`Scan the bin where you found ${look.scope.itemNo}.`); return; }
        const tracked = rows.some((x) => x.lotNo || x.serialNo);
        feedback.ok();
        if (tracked) { setAdding({ itemNo: look.scope.itemNo, binCode: r.bin.code, lotNo: "", needLot: true }); return; }
        add({ itemNo: look.scope.itemNo, binCode: r.bin.code, lotNo: "", needLot: false }, look.scope.description, look.scope.uom);
      }
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  };
  const add = (a: { itemNo: string; binCode: string; lotNo: string; needLot: boolean }, description = "", uom = "") => {
    const row: Row = { binCode: a.binCode, itemNo: a.itemNo, variantCode: "", lotNo: a.lotNo, serialNo: "", description, uom, expected: null, counted: "", added: true };
    if (rows.some((x) => keyOf(x) === keyOf(row))) { setErr("That's already on the list."); setAdding(null); return; }
    setRows((rs) => [...rs, row]); setAdding(null); feedback.ok();
  };

  // ----- the result -----
  if (result && look) {
    const diffs = result.lines.filter((l) => l.diff !== 0);
    return (
      <>
        <Header title="Quick count" sub={look.scope.kind === "bin" ? `Bin ${look.scope.binCode}` : look.scope.itemNo} back={reset} right={<LocChip code={loc} />} />
        <div className="w-body center">
          <span className={`w-bigtick ${result.status === "review" ? "warn" : ""}`}><I n={result.status === "review" ? "warn" : "check"} s={60} w={2.6} /></span>
          <h1 className="w-h1">{result.status === "matched" ? "Matches Business Central" : result.status === "posted" ? "Difference posted" : "Sent for review"}</h1>
          <p className="w-muted">
            {result.status === "matched" ? "Every line matched. It's recorded as counted."
              : result.status === "posted" ? `${diffs.length} line${diffs.length === 1 ? "" : "s"} adjusted in Business Central, ${money(result.net, true)}. Document ${result.docNo}.`
              : result.error ? `Business Central didn't take it: ${result.error} The lead will look at it.`
              : `${money(result.gross)} difference, over the ${money(result.limit)} you can post here. The lead reviews it.`}
          </p>
          {diffs.length > 0 && (
            <div className="w-card w-rows" style={{ alignSelf: "stretch" }}>
              {diffs.map((l, i) => (
                <div key={i} className="w-line">
                  <span className="w-row-main"><span className="w-strong">{l.itemNo}{look.scope.kind === "item" ? ` in ${l.binCode}` : ""}</span>
                    <span className="w-muted">{[lotText(l), `Expected ${fmt(l.expected)}, counted ${fmt(l.counted)}`].filter(Boolean).join(" · ")}</span></span>
                  <span className="w-strong">{l.diff > 0 ? "+" : ""}{fmt(l.diff)}</span>
                </div>
              ))}
            </div>
          )}
          {result.status === "review" && <button type="button" className="w-btn" style={{ alignSelf: "stretch" }} onClick={() => { setReplaces(result.id); setResult(null); setRows((rs) => rs.map((r) => ({ ...r, counted: "" }))); }}>Count it again</button>}
          <button type="button" className="w-btn primary" style={{ alignSelf: "stretch" }} onClick={reset}>Quick count something else</button>
        </div>
      </>
    );
  }

  // ----- counting what was scanned -----
  if (look) {
    const s = look.scope;
    return (
      <>
        <Header title="Quick count" sub={replaces ? "Counting again" : look.blind ? "Count what's there · blind" : "Count what's there"} back={reset} right={<LocChip code={loc} />} />
        <div className="w-body">
          <div className="w-hero"><span className="w-hero-l">{s.kind === "bin" ? "Bin" : "Item"}</span><span className="w-hero-c">{s.kind === "bin" ? s.binCode : s.itemNo}</span>
            <span className="w-hero-s">{s.kind === "bin" ? [s.zone && `Zone ${s.zone}`, s.blocked && `Blocked: ${s.blocked.toLowerCase()}`].filter(Boolean).join(" · ") || " " : s.description}</span></div>
          {look.flags.length > 0 && <Alert tone="warn" title="Flagged for a count">{look.flags.map((f) => `${f.bin}${f.source ? ` by ${f.source}` : ""}`).join(", ")}</Alert>}
          {err && <Alert title="Not yet">{err}</Alert>}
          <div className="w-label-row"><span className="w-label">{rows.length ? "Enter how many you count. 0 if it isn't there." : `Business Central shows nothing ${s.kind === "bin" ? "in this bin" : "of this item here"}.`}</span></div>
          <div className="w-card w-rows">
            {rows.map((r, i) => (
              <label key={keyOf(r)} className="w-line" htmlFor={`qc-${i}`}>
                <span className="w-row-main"><span className="w-strong">{s.kind === "bin" ? r.itemNo : <span className="w-mono">{r.binCode}</span>}</span>
                  <span className="w-muted">{[s.kind === "bin" ? r.description : null, lotText(r), r.added && "Found", r.expected !== null && `BC ${fmt(r.expected)}`].filter(Boolean).join(" · ")}</span></span>
                <input id={`qc-${i}`} className="w-qc-input" inputMode="decimal" value={r.counted} onChange={(e) => set(i, e.target.value)} placeholder="0" aria-label={`Counted ${r.itemNo} ${r.binCode}`} />
              </label>
            ))}
          </div>
          {adding?.needLot && <Alert tone="warn" title={`${adding.itemNo} is tracked by lot`}>Scan the lot number on what you found.</Alert>}
        </div>
        <div className="w-foot">
          <ScanField label={adding?.needLot ? "Scan the lot number" : s.kind === "bin" ? "Found something else? Scan the item" : "Found it in another bin? Scan the bin"} onScan={scanExtra} busy={busy} autoFocus={false} />
          <button type="button" className="w-btn primary" disabled={busy || (!allEntered && rows.length > 0) || (!rows.length)} onClick={submit}>Submit the count</button>
        </div>
      </>
    );
  }

  // ----- scan something -----
  return (
    <>
      <Header title="Quick count" sub="A bin or an item, right now" back="/wms" right={<LocChip code={loc} />} />
      <div className="w-body">
        {!flags.length && !recent.length && (
          <div className="w-prompt small">
            <span className="w-prompt-i"><I n="quick" s={52} w={1.6} /></span>
            <h1>Scan a bin or an item</h1>
            <p>Count what's there now. A match is recorded, and a small difference is posted on the spot.</p>
          </div>
        )}
        <ScanField label="Scan a bin or an item" onScan={scan} busy={busy} />
        {err && <Alert title="Couldn't count that">{err}</Alert>}
        {flags.length > 0 && (
          <div className="w-section"><div className="w-label">Bins to check</div>
            <div className="w-card w-rows">
              {flags.slice(0, 8).map((f) => (
                <button key={f.binCode} type="button" className="w-line link" onClick={() => scan(f.binCode)}>
                  <span className="w-row-main"><span className="w-strong w-mono">{f.binCode}</span><span className="w-muted">{[f.itemNo, f.source && `Flagged by ${f.source}`].filter(Boolean).join(" · ")}</span></span>
                  <I n="chev" />
                </button>
              ))}
            </div></div>
        )}
        {recent.length > 0 && (
          <div className="w-section"><div className="w-label">Your recent quick counts</div>
            <div className="w-card w-rows">
              {recent.slice(0, 5).map((q) => (
                <div key={q.id} className="w-line">
                  <span className="w-row-main"><span className="w-strong w-mono">{q.binCode ?? q.itemNo}</span>
                    <span className="w-muted">{q.status === "matched" ? "Matched" : q.status === "posted" ? `Posted ${money(q.value, true)}` : q.status === "review" ? "Waiting for review" : q.status === "rejected" ? "Left as it was" : "Replaced"}</span></span>
                  <span className="w-muted">{new Date(q.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                </div>
              ))}
            </div></div>
        )}
        <Link to="/wms/count" className="w-small">Count sheets</Link>
      </div>
    </>
  );
}
